import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import { StampGrid } from "./StampGrid";
import { LOYALTY_DISMISS_KEY, LOYALTY_EMAIL_KEY, OPEN_LOYALTY_EVENT } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_OK = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i;

const Sent = ({ email, onClose }) => (
  <div data-testid="loyalty-popup-success">
    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 220, damping: 14 }}
      className="w-14 h-14 rounded-full bg-[#F7F2EC] text-[#0B1F3A] flex items-center justify-center mb-5">
      <Check className="w-7 h-7" strokeWidth={2.5} />
    </motion.div>
    <h2 id="loyalty-popup-title" className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-3">Tessera in arrivo</h2>
    <p data-testid="loyalty-popup-sent-text" className="text-sm text-[#F7F2EC]/80 max-w-sm">
      Stiamo inviando la tua tessera BANY a <strong>{email}</strong>. Controlla anche la posta indesiderata. Mostra il QR in negozio a ogni taglio.
    </p>
    <button data-testid="loyalty-popup-done" onClick={onClose} className="mt-6 rounded-full bg-[#F7F2EC] text-[#0B1F3A] px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors">
      Perfetto
    </button>
  </div>
);

const LoyaltyPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(() => localStorage.getItem(LOYALTY_EMAIL_KEY) || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onOpen = () => { setDone(false); setOpen(true); };
    window.addEventListener(OPEN_LOYALTY_EVENT, onOpen);
    const already = localStorage.getItem(LOYALTY_EMAIL_KEY) || sessionStorage.getItem(LOYALTY_DISMISS_KEY);
    const t = already ? null : setTimeout(() => setOpen(true), 2200);
    return () => { window.removeEventListener(OPEN_LOYALTY_EVENT, onOpen); if (t) clearTimeout(t); };
  }, []);

  const close = () => { sessionStorage.setItem(LOYALTY_DISMISS_KEY, "1"); setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_OK.test(value)) { setError("Inserisci un'email valida"); return; }
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/loyalty/join`, { email: value });
      localStorage.setItem(LOYALTY_EMAIL_KEY, value);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Non siamo riusciti a inviare la tessera, riprova");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="loyalty-popup-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-[#071528]/70 backdrop-blur-md px-3 pb-3 sm:p-6" onClick={close}>
          <motion.div data-testid="loyalty-popup" role="dialog" aria-modal="true" aria-labelledby="loyalty-popup-title"
            initial={{ y: 40, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 24, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-[#0B1F3A] text-[#F7F2EC] shadow-[0_30px_90px_rgba(0,0,0,0.5)] noise-overlay">
            <button data-testid="loyalty-popup-close" onClick={close} aria-label="Chiudi"
              className="absolute right-4 top-4 z-20 rounded-full border border-[#F7F2EC]/20 p-2 text-[#F7F2EC]/70 hover:text-[#F7F2EC] transition-colors">
              <X className="w-4 h-4" />
            </button>
            <div className="relative z-10 p-7 sm:p-10">
              <p className="font-mono-alt text-[11px] uppercase tracking-[0.35em] text-[#F7F2EC]/60 mb-4">Tessera fedeltà · BANY</p>
              {done ? <Sent email={email.trim().toLowerCase()} onClose={() => setOpen(false)} /> : (
                <form onSubmit={submit} noValidate>
                  <h2 id="loyalty-popup-title" className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.05] mb-3">
                    10 tagli,<br />poi il 50% di sconto
                  </h2>
                  <p className="text-sm text-[#F7F2EC]/75 max-w-sm mb-6">Lascia la tua email: ricevi subito la tessera con il QR da mostrare in negozio.</p>
                  <div className="mb-6 max-w-xs"><StampGrid stamps={3} dark prefix="loyalty-popup-stamp" /></div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input data-testid="loyalty-popup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="nome@email.it" aria-label="La tua email"
                      className="flex-1 rounded-full border border-[#F7F2EC]/25 bg-[#F7F2EC]/5 px-5 py-3.5 text-sm text-[#F7F2EC] placeholder:text-[#F7F2EC]/40 outline-none focus:border-[#F7F2EC] transition-colors" />
                    <button type="submit" data-testid="loyalty-popup-submit" disabled={busy}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F7F2EC] text-[#0B1F3A] px-6 py-3.5 text-xs font-bold uppercase tracking-[0.15em] hover:bg-white disabled:opacity-60 transition-colors">
                      {busy && <Loader2 className="w-4 h-4 animate-spin" />} Ricevi tessera
                    </button>
                  </div>
                  {error && <p data-testid="loyalty-popup-error" className="text-xs text-[#FFB38A] mt-3">{error}</p>}
                  <p className="text-[11px] text-[#F7F2EC]/45 mt-4">Usiamo l'email solo per inviarti la tessera.</p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoyaltyPopup;
